import { Tooltip } from "@octopusdeploy/design-system-components";
import { Permission, TaskState, type TimelineItem } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type { TimelineOptionSelectedEvent } from "~/analytics/Analytics";
import { TimelineOptionAction, useAnalyticTimelineOptionSelectedDispatch } from "~/analytics/Analytics";
import { repository } from "~/clientInstance";
import InternalLink from "~/components/Navigation/InternalLink";
import { OverflowMenuItems, OverflowMenu } from "~/components/OverflowMenu/OverflowMenu";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import DateFormatter from "~/utils/DateFormatter/DateFormatter";
import TaskStatusIcon from "../../../TaskStatusIcon";
import { deploymentStatus, deploymentTitle, menu, statusColumn, statusIcon, timelineRow, timestamp, historicalDeploymentTitle } from "./styles";
interface TimelineRowProps {
    item: TimelineItem;
    referringReleaseId: string;
}
export const TimelineRow = (props: TimelineRowProps) => {
    const dispatchAnalyticsAction = useAnalyticTimelineOptionSelectedDispatch();
    const createTrackedNavItem = (text: string, path: LinkHref, anaylitcsData: TimelineOptionSelectedEvent, permission?: PermissionCheckProps) => {
        return OverflowMenuItems.navItem(text, path, permission, () => {
            dispatchAnalyticsAction(anaylitcsData);
        }, true);
    };
    const handleDeleteConfirm = async () => {
        const toDelete = await repository.Deployments.get(props.item.Id);
        await repository.Deployments.del(toDelete);
        const analyticsData: TimelineOptionSelectedEvent = {
            deploymentId: props.item.Id,
            releaseId: props.referringReleaseId,
            environmentId: props.item.EnvironmentId,
            projectId: props.item.ProjectId,
            selectedOption: TimelineOptionAction.Delete,
            selectedOptionReleaseId: props.item.ReleaseId,
        };
        dispatchAnalyticsAction(analyticsData);
    };
    const getStateText = (item: TimelineItem): string => {
        if (item.HasPendingInterruptions) {
            return "requires manual intervention";
        }
        switch (item.TaskState) {
            case TaskState.Queued:
            case TaskState.Executing:
            case TaskState.Failed:
            case TaskState.Canceled:
            case TaskState.Cancelling:
                return item.TaskState.toLowerCase();
            case TaskState.TimedOut:
                return "timed out";
            case TaskState.Success:
                return "successful";
        }
    };
    const getTitle = (item: TimelineItem) => {
        if (item.IsHistorical) {
            return (<Tooltip content="This release has been deleted" position="top">
                    <span className={historicalDeploymentTitle}>
                        {item.Version} {item.Iteration}
                    </span>
                </Tooltip>);
        }
        const content = (<span className={deploymentTitle}>
                {item.Version} {item.Iteration}
            </span>);
        const pageUrl = links.deploymentPage.generateUrl({ spaceId: item.SpaceId, deploymentId: item.Id, projectSlug: item.ProjectId, releaseVersion: item.Version });
        return <InternalLink to={pageUrl}>{content}</InternalLink>;
    };
    const getOverflowMenuItems = () => {
        const analyticsData: Pick<TimelineOptionSelectedEvent, "deploymentId" | "releaseId" | "environmentId" | "projectId" | "selectedOptionReleaseId"> = {
            deploymentId: props.item.Id,
            releaseId: props.referringReleaseId,
            environmentId: props.item.EnvironmentId,
            projectId: props.item.ProjectId,
            selectedOptionReleaseId: props.item.ReleaseId,
        };
        const redeployItemAnalyticsData: TimelineOptionSelectedEvent = { ...analyticsData, selectedOption: TimelineOptionAction.Redeploy };
        const redeployItem = createTrackedNavItem("Redeploy...", links.deployReleasePage.generateUrl({ spaceId: props.item.SpaceId, projectSlug: props.item.ProjectId, releaseVersion: props.item.Version }, { previousDeploymentId: props.item.Id }), redeployItemAnalyticsData, { permission: Permission.DeploymentCreate, project: props.item.ProjectId, environment: props.item.EnvironmentId, tenant: props.item.TenantId });
        const deployToAnalyticsData: TimelineOptionSelectedEvent = { ...analyticsData, selectedOption: TimelineOptionAction.Deploy };
        const deployToItem = createTrackedNavItem("Deploy to...", links.deployReleasePage.generateUrl({ spaceId: props.item.SpaceId, projectSlug: props.item.ProjectId, releaseVersion: props.item.Version }), deployToAnalyticsData, {
            permission: Permission.DeploymentCreate,
            project: props.item.ProjectId,
            spaceScope: { spaceId: props.item.SpaceId },
        });
        const deleteDeploymentItem = OverflowMenuItems.deleteItem("Delete deployment...", "Are you sure you want to delete this deployment?", handleDeleteConfirm, <div>
                <p>Deleting this deployment is permanent. There is no going back.</p>
                <p>Do you wish to continue?</p>
            </div>, { permission: Permission.DeploymentDelete });
        const showTaskLogAnalyticsData: TimelineOptionSelectedEvent = { ...analyticsData, selectedOption: TimelineOptionAction.TaskLog };
        const taskLogItem = createTrackedNavItem("View task log", links.deploymentPage.generateUrl({ spaceId: props.item.SpaceId, deploymentId: props.item.Id, projectSlug: props.item.ProjectId, releaseVersion: props.item.Version }, { activeTab: "taskLog" }), showTaskLogAnalyticsData);
        return [redeployItem, deployToItem, deleteDeploymentItem, taskLogItem];
    };
    return (<div className={timelineRow}>
            <div className={timestamp}>
                <div>{DateFormatter.dateToCustomFormat(props.item.Created, "dddd")}</div>
                <div>{DateFormatter.dateToCustomFormat(props.item.Created, "LL")}</div>
                <div>{DateFormatter.dateToCustomFormat(props.item.Created, "hh:mm A Z")}</div>
            </div>
            <div className={statusIcon}>
                <TaskStatusIcon size={28} iconOnly={true} item={{ State: props.item.TaskState, PendingInterruptionTypes: props.item.PendingInterruptionTypes, IsCompleted: props.item.IsCompleted, HasWarningsOrErrors: props.item.HasWarningsOrErrors }}/>
            </div>
            <div className={statusColumn}>
                {getTitle(props.item)}
                <div className={deploymentStatus}>Deployment {getStateText(props.item)}</div>
            </div>
            <div className={menu}>{!props.item.IsHistorical ? <OverflowMenu menuItems={getOverflowMenuItems()} key={props.item.Id}/> : <></>}</div>
        </div>);
};
